import { DomainEvent } from './DomainEvent'
import { NewBuilderDomainEvent } from './types'
import {
  CreatedFields,
  deletedEventSchema,
  DeletedEventSchema,
  ProfileFields,
  renewCreatedFields,
  SchemaWithId,
} from '@soniq/schema'
import { uuidv7 } from '@pogokid/util'
import { throwOnResponseError } from '../Response'
import { ResourceRequestOptions, UpsertSchema } from '../types'
import { ProjectionEntityStore } from './ProjectionEntityStore'

export type EntityDomainEventType =
  | 'created'
  | 'updated'
  | 'deleted'
  | 'upserted'

export class EntityEventsBuilder<
  Schema extends SchemaWithId<CreatedFields & ProfileFields>
> {
  constructor(
    public entity: string,
    public version: number,
    public store: ProjectionEntityStore<Schema>
  ) {}

  static new<
    Schema extends SchemaWithId<CreatedFields & ProfileFields>
  >(
    entity: string,
    version: number,
    store: ProjectionEntityStore<Schema>
  ): EntityEventsBuilder<Schema> {
    return new EntityEventsBuilder<Schema>(
      entity,
      version,
      store
    )
  }

  eventType(type: EntityDomainEventType) {
    return `${this.entity}_${type}`
  }

  /**
   * Apply the event projection
   *
   * @param options
   * @param event
   */
  async applyEvent(
    options: ResourceRequestOptions,
    event: DomainEvent
  ) {
    switch (event.data.event_type) {
      case this.eventType('created'): {
        await throwOnResponseError(
          this.store.create(
            options,
            event.profile_id,
            event.data.user_id,
            event.data.payload as Schema
          )
        )
        break
      }
      case this.eventType('updated'): {
        await throwOnResponseError(
          this.store.update(
            options,
            event.profile_id,
            event.data.payload as SchemaWithId<Partial<Schema>>
          )
        )
        break
      }
      case this.eventType('deleted'): {
        await this.store.delete(
          options,
          event.profile_id,
          event.data.user_id,
          [(event.data.payload as DeletedEventSchema).id]
        )
        break
      }
      case this.eventType('upserted'): {
        console.warn(
          'Upserted event type is deprecated',
          event.event_type
        )
        await throwOnResponseError(
          this.store.upsert(
            options,
            event.profile_id,
            event.data.payload as UpsertSchema<Schema>[]
          )
        )
        break
      }
    }
  }

  async updatedOrCreated(
    resourceOptions: ResourceRequestOptions,
    profileId: string,
    userId: string,
    data: NewBuilderDomainEvent<Schema>['payload']
  ) {
    const payload = data as Record<string, unknown>
    if (payload['deleted_at'] || payload['deleted_by']) {
      throw new Error(
        `updatedOrCreated called with a deleted payload on entity "${this.entity}". Use deleted() instead.`
      )
    }
    const existing = data.id
      ? (
          await this.store.fetchById(
            resourceOptions,
            profileId,
            data.id
          )
        ).data
      : null
    if (existing) {
      return this.updated({
        profile_id: profileId,
        user_id: userId,
        payload: data,
      })
    } else {
      return this.created({
        profile_id: profileId,
        user_id: userId,
        payload: data,
      })
    }
  }

  created(event: NewBuilderDomainEvent<Schema>) {
    const { data, error } = this.store.validateToResponse(
      event.profile_id,
      renewCreatedFields(event.user_id, {
        deleted_by: null,
        deleted_at: null,
        ...event.payload,
        id: event.payload.id ?? uuidv7(),
      })
    )
    if (error) throw error
    if (!data) throw new Error('Invalid event')

    return DomainEvent.fromNewPayload({
      profile_id: event.profile_id,
      user_id: event.user_id,
      correlation_id: event.correlation_id ?? null,
      aggregate_type: 'profile',
      event_type: this.eventType('created'),
      event_version: this.version,
      payload: data,
      metadata: event.metadata,
    })
  }

  /**
   * @deprecated use create or update
   */
  upserted(
    event: NewBuilderDomainEvent<UpsertSchema<Schema>[]>
  ) {
    const {
      isValid,
      errors,
      cleanedValues: data,
    } = this.store.schema.validateMany(
      event.payload.map((entry) =>
        renewCreatedFields(event.user_id, {
          ...entry,
          id: entry.id ?? uuidv7(),
        })
      )
    )
    if (!isValid || !data) {
      throw new Error(
        `Validation failed: ${JSON.stringify(errors, null, 2)}`
      )
    }

    return DomainEvent.fromNewPayload({
      profile_id: event.profile_id,
      user_id: event.user_id,
      correlation_id: event.correlation_id ?? null,
      aggregate_type: 'profile',
      event_type: this.eventType('upserted'),
      event_version: this.version,
      payload: data,
      metadata: event.metadata,
    })
  }

  updated(
    event: NewBuilderDomainEvent<SchemaWithId<Partial<Schema>>>
  ) {
    const { data, error } = this.store.validateToResponse(
      event.profile_id,
      event.payload,
      { allowPartial: true }
    )
    if (error) throw error
    if (!data) throw new Error('Invalid event data')

    return DomainEvent.fromNewPayload({
      profile_id: event.profile_id,
      user_id: event.user_id,
      correlation_id: event.correlation_id ?? null,
      aggregate_type: 'profile',
      event_type: this.eventType('updated'),
      event_version: this.version,
      payload: data,
      metadata: event.metadata,
    })
  }

  deleted(event: NewBuilderDomainEvent<DeletedEventSchema>) {
    const { isValid, errors, cleanedValues } =
      deletedEventSchema.validateFn(event.payload)
    if (!isValid) {
      throw new Error(
        `Validation failed: ${JSON.stringify(errors, null, 2)}`
      )
    }
    if (!cleanedValues) throw new Error('Invalid event data')

    return DomainEvent.fromNewPayload({
      profile_id: event.profile_id,
      user_id: event.user_id,
      correlation_id: event.correlation_id ?? null,
      aggregate_type: 'profile',
      event_type: this.eventType('deleted'),
      event_version: this.version,
      payload: cleanedValues,
      metadata: event.metadata,
    })
  }
}
